import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {useNeuromuscularDiagnosis} from '@/features/patients/services/useNeuromuscularDiagnosis'
import {useTranslation} from '@/translations/i18n'
import {NeuroMusocolarDiagnosis} from '@/features/patients/types'
import {useRef} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Button} from '@/components/ui/button/Button'
import {XCloseIcon} from '@/components/ui/icon/Icon'

interface InputSelectNeuromuscularDiagnosisProps {
    label: string
    value?: NeuroMusocolarDiagnosis | null
    onChange: (value: NeuroMusocolarDiagnosis | null) => void
    placeholder?: string
    isOptional?: boolean
    errorMessage?: string
}

export const InputSelectNeuromuscularDiagnosis = ({
    label,
    placeholder,
    value,
    onChange,
    errorMessage,
    isOptional
}: InputSelectNeuromuscularDiagnosisProps) => {
    const {t} = useTranslation()
    const inputNeuromuscularDiagnosisRef = useRef(null)
    const neuromuscularDiagnosisQuery = useNeuromuscularDiagnosis({
        limit: 10,
        responseType: 'small'
    })

    return (
        <ComboboxProvider setValue={neuromuscularDiagnosisQuery.onSearch} resetValueOnHide>
            <SelectProvider
                value={value?.name ?? ''}
                setValue={value =>
                    onChange(
                        neuromuscularDiagnosisQuery.remappedData.find(
                            neuromuscularDiagnosis => neuromuscularDiagnosis.name == value
                        ) ?? null
                    )
                }
                setOpen={() => neuromuscularDiagnosisQuery.onResetSearch(inputNeuromuscularDiagnosisRef)}
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    <Label>
                        {label} {isOptional && <span className="description">({t('commons:optional')})</span>}
                    </Label>
                    <SelectTrigger
                        placeholder={placeholder}
                        fullWidth
                        value={value?.id}
                        displayValue={() => (
                            <Flexbox fullWidth align="center" justify="space-between">
                                {value?.name}
                                <Button variant="ghost" size="sm" shape="text" onClick={() => onChange(null)}>
                                    <XCloseIcon />
                                </Button>
                            </Flexbox>
                        )}
                        hasError={!!errorMessage}
                    />
                    <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                        <InputStatusIcon hasError={!!errorMessage} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>
                    <SelectComboboxPopover sameWidth>
                        <SelectComboboxInput ref={inputNeuromuscularDiagnosisRef} placeholder={t('commons:select')} />
                        <SelectComboboxList>
                            {neuromuscularDiagnosisQuery.isLoading ? (
                                <>
                                    {Array.from({length: 6}, (_, index) => (
                                        <Skeleton key={index} height={32} />
                                    ))}
                                </>
                            ) : neuromuscularDiagnosisQuery.isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : neuromuscularDiagnosisQuery.remappedData.length > 0 ? (
                                <SelectVirtualList
                                    data={neuromuscularDiagnosisQuery.remappedData}
                                    totalCount={neuromuscularDiagnosisQuery.remappedData.length}
                                    overscan={100}
                                    endReached={() =>
                                        neuromuscularDiagnosisQuery.hasNextPage &&
                                        neuromuscularDiagnosisQuery.fetchNextPage()
                                    }
                                    itemContent={(itemIndex, item) => (
                                        <SelectItem hasItemCheckbox key={itemIndex} value={item.name}>
                                            {item.name}
                                        </SelectItem>
                                    )}
                                />
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
